import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Building2, Globe } from 'lucide-react';
// Logo images
const logoWhite = "/Logos/Sayaara White Logo.png";
import { Button } from "../ui/button";
import { useLanguage } from "../../contexts/LanguageContext";

interface NavigationProps {
  onContactClick: () => void;
}

function MobileNavigation({ 
  isOpen, 
  onClose, 
  onContactClick 
}: NavigationProps & { isOpen: boolean; onClose: () => void }) {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  
  if (!isOpen) return null;

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/listings') return 'listings';
    if (path === '/about') return 'about';
    if (path === '/how-it-works') return 'how-it-works';
    return 'home';
  };

  const currentPage = getCurrentPage();

  const menuItems = [
    { name: t('nav.home'), page: 'home', path: '/' },
    { name: t('nav.listings'), page: 'listings', path: '/listings' },
    { name: t('nav.howItWorks'), page: 'how-it-works', path: '/how-it-works' },
    { name: t('nav.about'), page: 'about', path: '/about' }
  ];

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="fixed inset-0 bg-transparent" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-64 bg-white shadow-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <img src={logoWhite} alt="Sayaara" className="h-8" />
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={20} />
          </Button>
        </div>
        <nav className="p-4">
          {menuItems.map((item) => (
            <Link
              key={item.page}
              to={item.path}
              onClick={onClose}
              className={`block w-full text-left py-3 px-2 rounded-lg transition-all duration-200 ${
                currentPage === item.page
                  ? 'text-[#f91bdc] bg-[#f91bdc]/10'
                  : 'text-gray-700 hover:text-[#f91bdc] hover:bg-gray-50'
              }`}
            >
              {item.name}
            </Link>
          ))}
          
          {/* Language toggle for mobile */}
          <button
            onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
            className="w-full text-left py-3 px-2 rounded-lg transition-all duration-200 text-gray-700 hover:text-[#f91bdc] hover:bg-gray-50 flex items-center"
          >
            <Globe size={16} className="mr-2" />
            {t('nav.language')}
          </button>
          
          {/* Dealer Portal button for mobile */}
          <Link
            to="/dealer"
            onClick={onClose}
            className="w-full text-left py-3 px-2 rounded-lg transition-all duration-200 text-gray-700 hover:text-[#f91bdc] hover:bg-gray-50 flex items-center"
          >
            <Building2 size={16} className="mr-2" />
            {t('nav.dealerPortal')}
          </Link>
          
          <Button 
            onClick={() => {
              onContactClick();
              onClose();
            }}
            className="w-full mt-4 bg-[#f91bdc] hover:bg-[#e015c9]"
          >
            {t('nav.contact')}
          </Button>
        </nav>
      </div>
    </div>
  );
}

export function NavigationBar({ onContactClick }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/listings') return 'listings';
    if (path === '/about') return 'about';
    if (path === '/how-it-works') return 'how-it-works';
    return 'home';
  };

  const currentPage = getCurrentPage();

  const menuItems = [
    { name: t('nav.home'), page: 'home', path: '/' },
    { name: t('nav.listings'), page: 'listings', path: '/listings' },
    { name: t('nav.howItWorks'), page: 'how-it-works', path: '/how-it-works' },
    { name: t('nav.about'), page: 'about', path: '/about' }
  ];

  return (
    <>
      <nav className="bg-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex-shrink-0 cursor-pointer">
              <img 
                src={logoWhite} 
                alt="Sayaara" 
                className="h-[46px] w-auto transition-transform duration-200 hover:scale-105" 
              />
            </Link>

            <div className="hidden lg:flex items-center space-x-8">
              {menuItems.map((item) => (
                <Link
                  key={item.page}
                  to={item.path}
                  className={`px-3 py-2 text-sm font-medium transition-all duration-200 relative ${
                    currentPage === item.page
                      ? 'text-[#f91bdc]'
                      : 'text-gray-700 hover:text-[#f91bdc]'
                  }`}
                >
                  {item.name}
                  {currentPage === item.page && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#f91bdc] transition-all duration-300" />
                  )}
                </Link>
              ))}
              
              {/* Language toggle for desktop */}
              <button
                onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
                className="px-3 py-2 text-sm font-medium transition-all duration-200 text-gray-700 hover:text-[#f91bdc] flex items-center border border-gray-200 rounded-lg hover:border-[#f91bdc]/30"
              >
                <Globe size={16} className="mr-2" />
                {t('nav.language')}
              </button>
              
              {/* Dealer Portal button for desktop */}
              <Link
                to="/dealer"
                className="px-3 py-2 text-sm font-medium transition-all duration-200 text-gray-700 hover:text-[#f91bdc] flex items-center border border-gray-200 rounded-lg hover:border-[#f91bdc]/30"
              >
                <Building2 size={16} className="mr-2" />
                {t('nav.dealerPortal')}
              </Link>
              
              <Button 
                onClick={onContactClick}
                className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-6"
              >
                {t('nav.contact')}
              </Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </Button>
          </div>
        </div>
      </nav>

      <MobileNavigation
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onContactClick={onContactClick}
      />
    </>
  );
}